import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, RotateCcw, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { OnOffSwitch } from "@/components/OnOffSwitch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FieldWrap, NativeSelect } from "@/components/Field";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { ResetDialog } from "@/components/admin/ResetDialog";
import { MyInfoDialog } from "@/components/MyInfoDialog";
import { PasswordDialog } from "@/components/Layout";
import { LoginHistory } from "@/components/admin/LoginHistory";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";

const TITLES = ["Pastor", "Deacon", "Leader", "Secretary", "Treasurer", "Assigned"];
const EMPTY = { name: "", email: "", password: "", role: "church_user", church_id: "", title: "Pastor", active: true, admin_areas: ["all"] };
export const AREAS = [
  ["records", "Tithes, Progress & Conference Collection", "View, add, edit and delete records of all churches"],
  ["reports", "Reports", "View, print and export reports of all churches"],
  ["conference", "Conference", "Conference events and all registrations"],
  ["users", "Users & Logins", "See Users & Logins: their own login and the church logins they created"],
  ["churches", "Churches", "Add, edit, reorder and delete churches"],
  ["settings", "Lists, toggles & permissions", "Categories, collections, destinations, payment types, section toggles"],
  ["content", "Page content editor", "Edit banners, text and pictures on pages"],
  ["reminders", "Missing reports & reminders", "See missing reports, send reminders, get monthly summary"],
  ["finance", "Expenses & financial report", "Record expenses, set up INCOME/EXPENDITURES, balance forward"],
  ["export", "Export Data", "Download all reports, records and a full database backup"],
  ["receipts", "Receipts", "Create, view, print and resend receipts in the Receipt Book"],
  ["other", "Other Collection", "Add, view, edit and delete Other Collections (small events and activities)"],
  ["special", "Events Collection", "Add, view, edit and delete the General Council and MWJK Events Collection, their people and payments"],
];
const areaLabel = (a) => AREAS.find((x) => x[0] === a)?.[1] || a;
const SECTIONS = [
  ["tithes", "Tithe Report", "General Council & MWJK tithes"],
  ["progress", "Progress Report", "MWJK monthly progress report"],
  ["donations", "Conference Collection", "Conference Collection records"],
  ["conference", "Conference Registration", "Register members for events"],
  ["reports", "Reports", "Church summary, print and export"],
];
const ALL_SECTIONS = SECTIONS.map((s) => s[0]);

function SectionPicker({ value, onChange, allowed = ALL_SECTIONS, special, setSpecial, canSpecial }) {
  const toggle = (k, on) => onChange(on ? ALL_SECTIONS.filter((x) => x === k || value.includes(x)) : value.filter((x) => x !== k));
  return (
    <div className="space-y-2 rounded-lg border border-slate-200 bg-[hsl(var(--sand))] p-3" data-testid="user-section-picker">
      <div className="text-sm font-semibold text-slate-800">Sections this user can use</div>
      {SECTIONS.filter(([k]) => allowed.includes(k)).map(([k, l, h]) => (
        <div key={k} className="flex items-center justify-between gap-3 rounded-lg bg-white p-2.5">
          <span><span className="block text-sm font-semibold">{l}</span><span className="block text-xs text-slate-500">{h}</span></span>
          <OnOffSwitch checked={value.includes(k)} onChange={(on) => toggle(k, on)} testid={`user-section-${k}`} />
        </div>
      ))}
      {canSpecial && (
        <div className="space-y-2 rounded-lg bg-white p-2.5" data-testid="user-special-box">
          <div className="flex items-center justify-between gap-3">
            <span><span className="block text-sm font-semibold">Events Collection</span><span className="block text-xs text-slate-500">View only · their own church's people and payments</span></span>
            <OnOffSwitch checked={!!special.special_on} onChange={(on) => setSpecial({ special_on: on, special_all: on && !!special.special_all })} testid="user-section-special" />
          </div>
          {special.special_on && (
            <div className="ml-4 flex items-center justify-between gap-3 border-l-2 border-slate-200 pl-3">
              <span><span className="block text-sm font-semibold">See all churches</span><span className="block text-xs text-slate-500">View every church's people, payments and Summary</span></span>
              <OnOffSwitch checked={!!special.special_all} onChange={(on) => setSpecial({ ...special, special_all: on })} testid="user-section-special-all" />
            </div>
          )}
        </div>
      )}
      <p className="text-xs text-slate-500">Sections turned off for everyone in Toggles & Permissions stay hidden.</p>
    </div>
  );
}

function AccessPicker({ value, onChange }) {
  const full = value.includes("all");
  const toggle = (k, on) => onChange(on ? [...value.filter((x) => x !== "all"), k] : value.filter((x) => x !== k));
  return (
    <div className="space-y-3 rounded-lg border border-slate-200 bg-[hsl(var(--sand))] p-3" data-testid="user-access-picker">
      <div className="text-sm font-semibold text-slate-800">Admin access</div>
      <div className="grid grid-cols-2 gap-2">
        {[[true, "Full access", "Everything"], [false, "Limited access", "Only areas I pick"]].map(([isFull, l, s]) => (
          <button type="button" key={l} onClick={() => onChange(isFull ? ["all"] : [])} data-testid={`user-access-${isFull ? "full" : "limited"}`}
            className={`rounded-lg border-2 p-3 text-left ${full === isFull ? "border-[hsl(var(--primary))] bg-white" : "border-transparent bg-white/60"}`}>
            <div className="font-semibold">{l}</div><div className="text-xs text-slate-500">{s}</div>
          </button>
        ))}
      </div>
      {!full && AREAS.map(([k, l, h]) => (
        <div key={k} className="flex items-center justify-between gap-3 rounded-lg bg-white p-2.5">
          <span><span className="block text-sm font-semibold">{l}</span><span className="block text-xs text-slate-500">{h}</span></span>
          <OnOffSwitch checked={value.includes(k)} onChange={(on) => toggle(k, on)} testid={`user-area-${k}`} />
        </div>
      ))}
    </div>
  );
}

function MessagingOptions({ v, setV }) {
  const full = v.admin_areas.includes("all");
  const label = v.msg_show_name ? (v.msg_display_name || "").trim() || v.name || "Admin" : full ? "Master Admin" : "Admin";
  return (
    <div className="space-y-3 rounded-lg border border-slate-200 bg-[hsl(var(--sand))] p-3" data-testid="user-messaging-options">
      <div className="flex items-center justify-between gap-3 rounded-lg bg-white p-2.5">
        <span><span className="block text-sm font-semibold">Messaging access</span><span className="block text-xs text-slate-500">Send and receive messages with church users, including files and photos</span></span>
        <OnOffSwitch checked={!!v.messaging} onChange={(on) => setV({ ...v, messaging: on })} testid="user-messaging-switch" />
      </div>
      {v.messaging && (
        <>
          <div className="flex items-center justify-between gap-3 rounded-lg bg-white p-2.5">
            <span><span className="block text-sm font-semibold">Show real name to church users</span><span className="block text-xs text-slate-500">OFF shows the role label "{full ? "Master Admin" : "Admin"}"</span></span>
            <OnOffSwitch checked={!!v.msg_show_name} onChange={(on) => setV({ ...v, msg_show_name: on })} testid="user-msg-show-name-switch" />
          </div>
          {v.msg_show_name && (
            <FieldWrap label="Name shown to church users"><Input className="h-11 bg-white text-slate-900" maxLength={80} value={v.msg_display_name || ""} placeholder={v.name} onChange={(e) => setV({ ...v, msg_display_name: e.target.value })} data-testid="user-msg-display-name" /></FieldWrap>
          )}
          <p className="text-xs text-slate-600" data-testid="user-msg-label-preview">Church users will see: <span className="font-bold">{label}</span></p>
        </>
      )}
    </div>
  );
}

const SECTION_AREA = { tithes: "records", progress: "records", donations: "records", conference: "conference", reports: "reports" };
const LOGIN_SWITCHES = [
  ["can_edit_users", "Edit logins", "Fix and delete the church logins they created"],
  ["can_create_users", "Create church logins", "Make new church leader logins and give them access (never more than they have)"],
  ["can_reset", "Reset passwords", "Reset passwords of the church logins they created and unlock them"],
];

function UserDialog({ open, user, onClose, onSaved, admins }) {
  const { churches, isFullAdmin, hasArea, user: me } = useApp();
  const [v, setV] = useState(EMPTY);
  useEffect(() => {
    if (open) {
      const areas = user?.admin_areas?.length ? user.admin_areas : ["all"];
      const msg = { messaging: user?.messaging ?? (areas.includes("all") || areas.includes("messages")), msg_show_name: !!user?.msg_show_name, msg_display_name: user?.msg_display_name || "", can_reset: !!user?.can_reset, can_edit_users: !!user?.can_edit_users, can_create_users: !!user?.can_create_users };
      const mine = ALL_SECTIONS.filter((s) => isFullAdmin || hasArea(SECTION_AREA[s]));
      setV(user ? { ...user, ...msg, church_id: user.church_id || "", password: "", admin_areas: areas, owner_id: user.created_by || "", sections: Array.isArray(user.sections) ? user.sections : ALL_SECTIONS } : { ...EMPTY, ...msg, owner_id: "", sections: mine });
    }
  }, [open, user, isFullAdmin, hasArea, me.id]);
  const allowed = ALL_SECTIONS.filter((s) => isFullAdmin || hasArea(SECTION_AREA[s]) || (user?.sections || []).includes(s));
  const pwOk = isFullAdmin || (me.can_reset && user?.id !== me.id);
  const set = (k) => (val) => setV({ ...v, [k]: val?.target ? val.target.value : val });
  const save = async (e) => {
    e.preventDefault();
    if (v.role === "master_admin" && !v.admin_areas.length) { toast.error("Pick at least one area, or choose Full access"); return; }
    const body = { name: v.name, email: v.email, role: v.role, church_id: v.role === "church_user" ? v.church_id : null, title: v.title, active: v.active, password: v.password || null, username: v.username || null, admin_areas: v.role === "master_admin" ? v.admin_areas : [], sections: v.role === "church_user" ? v.sections : null, ...(v.role === "church_user" && (isFullAdmin || hasArea("special")) ? { special_on: !!v.special_on, special_all: !!v.special_on && !!v.special_all } : {}), ...(isFullAdmin && v.role === "church_user" ? { owner_id: v.owner_id || "" } : {}), ...(v.role === "master_admin" ? { reply_email: (v.reply_email || "").trim(), messaging: !!v.messaging, msg_show_name: !!v.msg_show_name, msg_display_name: v.msg_display_name || "", can_reset: !!v.can_reset, can_edit_users: !!v.can_edit_users, can_create_users: !!v.can_create_users } : {}) };
    try {
      if (user) await api.put(`/users/${user.id}`, body); else await api.post("/users", body);
      toast.success("User saved");
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-md overflow-y-auto" data-testid="user-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl">{user ? "Edit login" : "New login"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <FieldWrap label="Full name" required><Input className="h-11" value={v.name} onChange={set("name")} data-testid="user-name-input" /></FieldWrap>
          <FieldWrap label="Email" required><Input type="email" className="h-11" value={v.email} onChange={set("email")} data-testid="user-email-input" /></FieldWrap>
          {v.role === "master_admin" && (
            <FieldWrap label="Receipt reply email (optional)" sub="Receipt emails this admin sends show their name, and replies come here. Empty = the usual MWJK address.">
              <Input type="email" className="h-11" value={v.reply_email || ""} onChange={set("reply_email")} data-testid="user-reply-email-input" />
            </FieldWrap>
          )}
          <FieldWrap label="Username (optional)" sub="Can be used instead of email to log in. Letters, numbers, dot, dash or underscore.">
            <Input className="h-11" value={v.username || ""} onChange={set("username")} autoComplete="off" data-testid="user-username-input" placeholder="e.g. pastor.john" />
          </FieldWrap>
          {pwOk && (
          <FieldWrap label={user ? "Reset password (leave empty to keep current)" : "Temporary password (6+ characters)"} required={!user}
            sub={user && user.id === me.id ? undefined : "The user will be asked to choose their own password the next time they log in."}>
            <Input type="text" className="h-11" value={v.password} onChange={set("password")} data-testid="user-password-input" autoComplete="new-password" />
          </FieldWrap>
          )}
          {!pwOk && !user && (
            <FieldWrap label="Temporary password (6+ characters)" required sub="The user will be asked to choose their own password the next time they log in.">
              <Input type="text" className="h-11" value={v.password} onChange={set("password")} data-testid="user-password-input" autoComplete="new-password" />
            </FieldWrap>
          )}
          <FieldWrap label="Role">
            <NativeSelect value={v.role} onChange={(r) => setV({ ...v, role: r, title: r === "master_admin" ? "Master Admin" : v.title === "Master Admin" ? "Pastor" : v.title })} options={isFullAdmin ? [{ value: "church_user", label: "Local Church User" }, { value: "master_admin", label: "Admin" }] : [{ value: "church_user", label: "Local Church User" }]} testid="user-role-select" />
          </FieldWrap>
          {v.role === "master_admin" && <AccessPicker value={v.admin_areas} onChange={set("admin_areas")} />}
          {v.role === "master_admin" && <MessagingOptions v={v} setV={setV} />}
          {v.role === "master_admin" && (
            <div className="space-y-2 rounded-lg border border-slate-200 bg-[hsl(var(--sand))] p-3" data-testid="user-login-switches">
              <div className="text-sm font-semibold text-slate-800">Users & Logins permissions</div>
              <p className="text-xs text-slate-500">They work only when "Users & Logins" is ON in Admin access. Full access includes all of them.</p>
              {LOGIN_SWITCHES.map(([k, l, h]) => (
                <div key={k} className="flex items-center justify-between gap-3 rounded-lg bg-white p-2.5">
                  <span><span className="block text-sm font-semibold">{l}</span><span className="block text-xs text-slate-500">{h}</span></span>
                  <OnOffSwitch checked={!!v[k]} onChange={(on) => setV({ ...v, [k]: on })} testid={`user-${k.replace(/_/g, "-")}-switch`} />
                </div>
              ))}
            </div>
          )}
          {v.role === "church_user" && (
            <FieldWrap label="Church" required>
              <NativeSelect value={v.church_id} onChange={set("church_id")} placeholder="Choose church..." options={churches.map((c) => ({ value: c.id, label: c.name }))} testid="user-church-select" />
            </FieldWrap>
          )}
          {v.role === "church_user" && <SectionPicker value={v.sections} onChange={set("sections")} allowed={allowed} special={v} setSpecial={(x) => setV((o) => ({ ...o, ...x }))} canSpecial={isFullAdmin || hasArea("special")} />}
          {v.role === "church_user" && isFullAdmin && (
            <FieldWrap label="Managed by" sub="This admin can see this login (and edit or reset it if their switches are ON).">
              <NativeSelect value={v.owner_id || ""} onChange={set("owner_id")} placeholder="Only the Master Admin" options={admins.filter((a) => !(a.admin_areas || []).includes("all")).map((a) => ({ value: a.id, label: a.name }))} testid="user-owner-select" />
            </FieldWrap>
          )}
          <FieldWrap label="Title"><NativeSelect value={v.title} onChange={set("title")} options={[...new Set([...TITLES, "Master Admin", v.title].filter(Boolean))]} testid="user-title-select" /></FieldWrap>
          <label className="flex items-center justify-between rounded-lg border p-3 text-sm font-semibold">
            Account is active (can log in)
            <OnOffSwitch checked={v.active} onChange={set("active")} testid="user-active-switch" />
          </label>
          {user && isFullAdmin && <LoginHistory uid={user.id} createdAt={user.created_at} />}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" data-testid="user-save-btn">Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminUsers() {
  const { churches, user: me, isFullAdmin } = useApp();
  const [resetting, setResetting] = useState(null);
  const [selfDlg, setSelfDlg] = useState("");
  const [users, setUsers] = useState([]);
  const [churchFilter, setChurchFilter] = useState("");
  const [dialog, setDialog] = useState({ open: false, user: null });
  const load = useCallback(() => { api.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  useEffect(() => { load(); }, [load]);
  const remove = async (id) => { try { await api.delete(`/users/${id}`); toast.success("Login deleted"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const churchName = (id) => churches.find((c) => c.id === id)?.name || "—";
  const shown = users.filter((u) => !churchFilter || (churchFilter === "admins" ? u.role === "master_admin" : u.church_id === churchFilter));
  const admins = users.filter((u) => u.role === "master_admin");
  const mine = (u) => u.id === me.id;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="w-64"><NativeSelect value={churchFilter} onChange={setChurchFilter} placeholder="Everyone" testid="users-filter"
          options={[...(isFullAdmin ? [{ value: "admins", label: "Admins only" }] : []), ...churches.map((c) => ({ value: c.id, label: c.name }))]} /></div>
        {me.can_create_users && <Button onClick={() => setDialog({ open: true, user: null })} data-testid="add-user-btn"><Plus className="mr-2 h-4 w-4" /> Add login</Button>}
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="users-table">
          <thead><tr className="border-b bg-[hsl(var(--sand))] text-left">
            <th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Church</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th>
          </tr></thead>
          <tbody>
            {shown.map((u) => (
              <tr key={u.id} className="border-b border-slate-100" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3"><div className="font-semibold">{u.name}</div><div className="text-xs text-slate-500">{u.title}</div></td>
                <td className="px-4 py-3"><div>{u.email}</div>{u.username && <div className="text-xs text-slate-500" data-testid={`user-username-${u.id}`}>Username: {u.username}</div>}{u.must_change_password && <div className="text-xs text-amber-700">Using temporary password</div>}</td>
                <td className="px-4 py-3" data-testid={`user-access-${u.id}`}>{u.role === "master_admin" ? (
                  <div className="space-y-1">
                    <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-900">{!u.admin_areas?.length || u.admin_areas.includes("all") ? "Admin · Full access" : "Admin"}</span>
                    {u.admin_areas?.length > 0 && !u.admin_areas.includes("all") && <div className="max-w-56 text-xs text-slate-500">{u.admin_areas.map(areaLabel).join(", ")}</div>}
                  </div>
                ) : (
                  <div className="space-y-1">
                    <span>Church User</span>
                    {Array.isArray(u.sections) && u.sections.length < ALL_SECTIONS.length && <div className="max-w-56 text-xs text-amber-800" data-testid={`user-sections-${u.id}`}>Only: {u.sections.length ? SECTIONS.filter((s) => u.sections.includes(s[0])).map((s) => s[1]).join(", ") : "no sections"}</div>}
                  </div>
                )}</td>
                <td className="px-4 py-3">{u.role === "master_admin" ? "All churches" : churchName(u.church_id)}{isFullAdmin && u.role === "church_user" && <div className="text-xs text-slate-500" data-testid={`user-owner-${u.id}`}>Managed by: {admins.some((a) => a.id === u.created_by && !(a.admin_areas || []).includes("all")) ? u.created_by_name : "Master Admin"}</div>}</td>
                <td className="px-4 py-3">{u.active ? <span className="text-emerald-700">Active</span> : <span className="text-slate-400">Off</span>}</td>
                <td className="px-4 py-2"><div className="flex justify-end gap-1">
                  {mine(u) ? <Button variant="outline" size="sm" onClick={() => setSelfDlg("pw")} data-testid={`user-change-password-${u.id}`}><KeyRound className="mr-1 h-3.5 w-3.5" /> Password</Button>
                    : me.can_reset && <Button variant="outline" size="sm" onClick={() => setResetting(u)} data-testid={`user-reset-${u.id}`}><RotateCcw className="mr-1 h-3.5 w-3.5" /> Reset</Button>}
                  {(mine(u) || me.can_edit_users) && <Button variant="ghost" size="icon" onClick={() => (mine(u) && !isFullAdmin ? setSelfDlg("info") : setDialog({ open: true, user: u }))} data-testid={`user-edit-${u.id}`} aria-label="Edit login"><Pencil className="h-4 w-4" /></Button>}
                  {me.can_edit_users && !mine(u) && <ConfirmDelete onConfirm={() => remove(u.id)} title={`Delete login for ${u.name}?`} testid={`user-delete-${u.id}`} />}
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ResetDialog target={resetting} onClose={() => setResetting(null)} onDone={load} />
      <PasswordDialog open={selfDlg === "pw"} onClose={() => setSelfDlg("")} />
      <MyInfoDialog open={selfDlg === "info"} onClose={() => setSelfDlg("")} onSaved={load} />
      <UserDialog open={dialog.open} user={dialog.user} admins={admins} onClose={() => setDialog({ open: false, user: null })} onSaved={() => { setDialog({ open: false, user: null }); load(); }} />
    </div>
  );
}
